import React, { useState } from 'react';
import '../components/slimpass-colors.css';
import AdminLinks from './AdminLinks';

export interface Operadora {
  id: string;
  nome: string;
  logo: string;
  cor?: string;
}

interface OperadoraSelectorProps {
  operadoras: Operadora[];
  onSelect: (operadoraId: string) => void;
  onLogout: () => void;
}

// Função para gerar um logo colorido dinamicamente baseado na cor da operadora
const generateColoredLogo = (nome: string, cor: string = '#333333') => {
  const initials = nome.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase();
  
  // Criar SVG diretamente como string sem necessidade de base64
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23f5f5f5'/%3E%3Crect x='20' y='20' width='160' height='160' rx='10' fill='${cor.replace(/#/g, '%23')}' opacity='0.2'/%3E%3Crect x='40' y='40' width='120' height='120' rx='8' fill='${cor.replace(/#/g, '%23')}'/%3E%3Ctext x='100' y='115' font-family='Arial' font-size='48' font-weight='bold' text-anchor='middle' fill='white'%3E${initials}%3C/text%3E%3C/svg%3E`;
};

const OperadoraSelector: React.FC<OperadoraSelectorProps> = ({ operadoras, onSelect, onLogout }) => {
  const [showAdminLinks, setShowAdminLinks] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Função para verificar a senha de admin (simplificada)
  const checkAdminPassword = () => {
    // Senha simples para fins de demonstração - em produção usar algo mais seguro
    if (adminPassword === 'slimpassadmin') {
      setShowAdminLinks(true);
      setShowPasswordDialog(false);
      setPasswordError('');
    } else {
      setPasswordError('Senha incorreta');
    }
  };

  if (showAdminLinks) {
    return <AdminLinks operadoras={operadoras} onBack={() => setShowAdminLinks(false)} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative">
      {/* Botão de Logout no canto superior direito */}
      <div className="absolute top-4 right-4 z-10">
        <button 
          className="bg-white p-2 rounded-full shadow-md hover:bg-gray-100"
          onClick={onLogout}
          title="Sair"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </button>
      </div>
      
      <div className="max-w-4xl w-full space-y-8">
        <div>
          <img
            className="mx-auto h-16 w-auto"
            src="/images/Logo+slogan_light.png"
            alt="SlimPass Logo"
          />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Selecione a Operadora
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Escolha a operadora para visualizar o dashboard de diagnóstico
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {operadoras.map((operadora) => (
            <div
              key={operadora.id}
              className="bg-white overflow-hidden shadow-sm rounded-lg border-2 border-gray-200 hover:border-primary transition-all cursor-pointer"
              onClick={() => onSelect(operadora.id)}
              style={{ borderColor: operadora.cor }}
            >
              <div className="p-6 flex flex-col items-center">
                <div className="h-32 w-32 flex items-center justify-center mb-4">
                  <img
                    src={generateColoredLogo(operadora.nome, operadora.cor)}
                    alt={`${operadora.nome} Logo`}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <h3 className="text-lg font-medium text-gray-900">{operadora.nome}</h3>
              </div>
            </div>
          ))}
        </div>

        {/* Botão de Admin discreto */}
        <div className="flex justify-center mt-8">
          <button
            onClick={() => setShowPasswordDialog(true)}
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            Administração
          </button>
        </div>

        {/* Modal de senha para admin */}
        {showPasswordDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Acesso Administrativo</h3>
              <div className="mb-4">
                <label htmlFor="admin-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Senha
                </label>
                <input
                  type="password"
                  id="admin-password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                />
                {passwordError && (
                  <p className="mt-1 text-sm text-red-600">{passwordError}</p>
                )}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowPasswordDialog(false);
                    setAdminPassword('');
                    setPasswordError('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={checkAdminPassword}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-600"
                >
                  Acessar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OperadoraSelector; 